package com.evoapps.evoshift.ui.adaptive

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.LineBreak
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.ceil

/*
 * SH-130: wide tables whose column headers never truncate. A header may take two lines (balanced,
 * see [TableHeaderLineBreak]); the columns share the width by their weights, but a column never
 * gets less than its header needs in two lines or than its widest value needs in one. When the
 * width cannot give every column that, the screen shows its stacked (card) layout instead.
 */

/** Most lines a table header may take. */
const val TableHeaderMaxLines = 2

/** Line breaking for table headers: two lines of about the same length, not one long and one short. */
val TableHeaderLineBreak: LineBreak = LineBreak.Heading

/** One column of a table: its header, its share of the width and the values it must show whole. */
data class TableColumn(
    val header: String,
    val weight: Float,
    /** Values that must fit on one line (amounts, hours); names and the like may be shortened. */
    val values: List<String> = emptyList()
)

/**
 * The widths of [columns] in [available] width (the cells' text area plus [cellPadding] on each
 * side), or null when they do not fit: then the screen stacks its rows as cards. [headerStyle]
 * and [valueStyle] are the styles the header and value cells use.
 */
@Composable
fun rememberTableWidths(
    columns: List<TableColumn>,
    available: Dp,
    headerStyle: TextStyle,
    valueStyle: TextStyle,
    cellPadding: Dp
): List<Dp>? {
    val measurer = rememberTextMeasurer()
    val density = LocalDensity.current
    val needs = remember(columns, headerStyle, valueStyle, cellPadding, density) {
        columns.map { column ->
            maxOf(
                headerWidth(measurer, column.header, headerStyle, density),
                column.values.maxOfOrNull { singleLineWidth(measurer, it, valueStyle, density) } ?: 0.dp
            ) + cellPadding * 2
        }
    }
    return remember(needs, available, columns) { tableWidths(needs, columns.map { it.weight }, available) }
}

/**
 * Shares [available] between columns by [weights], giving each at least its [needs]; null when
 * the needs alone are more than the width. Pure, for tests.
 */
fun tableWidths(needs: List<Dp>, weights: List<Float>, available: Dp): List<Dp>? {
    require(needs.size == weights.size)
    if (needs.isEmpty()) return emptyList()
    if (needs.fold(0.dp) { sum, it -> sum + it } > available) return null
    // Columns whose weighted share is under their need take the need; the rest share what is
    // left by weight, again until every column has enough.
    val fixed = BooleanArray(needs.size)
    while (true) {
        val left = available - needs.indices.filter { fixed[it] }.fold(0.dp) { sum, i -> sum + needs[i] }
        val weightLeft = weights.indices.filter { !fixed[it] }.sumOf { weights[it].toDouble() }.toFloat()
        val short = needs.indices.filter { !fixed[it] && (if (weightLeft > 0f) left * (weights[it] / weightLeft) else 0.dp) < needs[it] }
        if (short.isEmpty()) {
            return needs.indices.map { i -> if (fixed[i]) needs[i] else left * (weights[i] / weightLeft) }
        }
        short.forEach { fixed[it] = true }
    }
}

/** The narrowest width that shows [text] in at most [TableHeaderMaxLines] lines, no word broken. */
private fun headerWidth(measurer: TextMeasurer, text: String, style: TextStyle, density: Density): Dp {
    val headerStyle = style.copy(lineBreak = TableHeaderLineBreak)
    val longestWord = text.split(' ').maxOf { singleLineWidth(measurer, it, style, density) }
    val whole = singleLineWidth(measurer, text, style, density)
    // The narrowest width that keeps the header to two lines without breaking a word.
    var low = longestWord
    var high = whole
    if (fits(measurer, text, headerStyle, low, density)) return low + RoundingSlack
    repeat(12) {
        val mid = (low + high) / 2
        if (fits(measurer, text, headerStyle, mid, density)) high = mid else low = mid
    }
    // The search ends right at the edge of a third line; the cell's width is rounded to whole
    // pixels when laid out, so keep a little room.
    return high + RoundingSlack
}

private val RoundingSlack = 3.dp

private fun fits(measurer: TextMeasurer, text: String, style: TextStyle, width: Dp, density: Density): Boolean {
    val px = with(density) { ceil(width.toPx()).toInt() }.coerceAtLeast(1)
    val layout = measurer.measure(text, style, constraints = Constraints(maxWidth = px), density = density)
    if (layout.lineCount > TableHeaderMaxLines) return false
    // No word may be split across lines: each line ends where a word ends.
    return (0 until layout.lineCount).all { line ->
        val end = layout.getLineEnd(line, visibleEnd = true)
        end >= text.length || text[end] == ' ' || text.getOrNull(end - 1) == ' '
    }
}

private fun singleLineWidth(measurer: TextMeasurer, text: String, style: TextStyle, density: Density): Dp {
    val layout = measurer.measure(text, style, softWrap = false, maxLines = 1, density = density)
    return with(density) { ceil(layout.size.width.toFloat()).toDp() } + 1.dp
}
